import PropTypes from 'prop-types';
import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import FavoriteIcon from 'src/common/components/shared/controls/favorite/FavoriteIcon';
import {
  FAVORITE,
  UNFAVORITE,
} from 'src/common/constants/localizations/profile';
import { useFavorite } from 'src/common/hooks/useFavorite';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import { selectIsDiscord, selectIsMobile } from 'src/common/selectors/app';
import iconCss from './favorite-icon.module.scss';

export function FavoriteIconControl({
  guideItem,
  followActionOrOption,
  fill,
  hoverFill,
  showIconLabel,
  isOptionsController = false,
  isOption = false,
  handleOptionClick,
  iconClassName,
  unFavoritedClassName,
  children,
}) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const isMobile = useSelector(selectIsMobile);
  const isDiscord = useSelector(selectIsDiscord);
  const [isHovering, setIsHovering] = useState(false);
  const [wasClickedOnHover, setWasClickedOnHover] = useState(false);
  const canControlHoveringLocally = !(isMobile || isDiscord);
  const { isFavorited, isLocked, toggleFavorite, canFollow, guideId } =
    useFavorite({
      guideItem,
      followActionOrOption,
    });

  function onClick() {
    if (isLocked) {
      return;
    }

    // Inverting the hover state on click to allow the icon to maintain the new state without optimistically rendering
    // the next state, which would be confusing UX. This inversion is undone when the mouse leaves the icon.
    setWasClickedOnHover(true);

    isOptionsController ? handleOptionClick?.() : toggleFavorite?.();
  }

  // Important: without useCallback, this onMouseEnter function can be triggered after a favorite click
  const onMouseEnter = useCallback(() => {
    if (canControlHoveringLocally && !wasClickedOnHover) {
      setIsHovering(true);
    }
  }, [canControlHoveringLocally, wasClickedOnHover]);

  const onMouseLeave = () => {
    setIsHovering(false);
    setWasClickedOnHover(false);
  };

  const containerProps = {
    'data-testid': `favorite-icon-control-${guideId}`,
    onClick,
    onMouseEnter,
    onMouseLeave,
  };

  if (!isOption) {
    containerProps.className = iconCss.container;
  }

  const isFavoritedLabel = isFavorited
    ? getLocalizedText(UNFAVORITE)
    : getLocalizedText(FAVORITE);
  const childrenProps = {
    guideId,
    showIconLabel,
    isFavorited,
    isFavoritedLabel: followActionOrOption?.title || isFavoritedLabel,
    isHovering: wasClickedOnHover ? !isHovering : isHovering,
    hoverFill,
    fill,
    iconClassName,
    unFavoritedClassName,
  };

  return canFollow ? (
    <div {...containerProps}>
      {children ? children(childrenProps) : <FavoriteIcon {...childrenProps} />}
    </div>
  ) : null;
}

FavoriteIconControl.propTypes = {
  guideItem: PropTypes.object,
  children: PropTypes.func,
  showIconLabel: PropTypes.bool,
  isOptionsController: PropTypes.bool,
  isOption: PropTypes.bool,
  fill: PropTypes.string,
  hoverFill: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
};
